import React, { useEffect, useRef, useState } from "react";
import { siteConfig } from "../siteConfig";

// The pilot sign-up form.
//
// This form really delivers. Netlify captures the submission for any form it
// found in the built HTML, which is why a hidden copy of these exact fields
// lives in public/index.html: the build-time parser reads static markup, not
// React output. Change a field name here and change it there, or the
// submission arrives with that field missing.
//
// The one rule this file exists to keep: a visitor is never told their
// enquiry arrived unless the server said so. Anything else, a non-2xx, a
// network failure, form handling switched off, shows an honest failure with
// another way to reach us. A page that swallows an enquiry and smiles is
// worse than a page with no form at all.

const FORM_NAME = "pilot-signup";

const ROLES = [
  "Course Manager",
  "Head Greenkeeper",
  "General Manager",
  "Club Secretary or Owner",
  "Something else",
];

const encode = (data) =>
  Object.keys(data)
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(data[k])}`)
    .join("&");

export default function PilotForm() {
  const [state, setState] = useState("idle"); // idle | sending | sent | failed
  const statusRef = useRef(null);

  // Move the reader to the outcome, once React has actually rendered it.
  // Focusing straight after setState reaches for a node that does not exist
  // yet, which is why this is an effect and not a callback at the call site.
  useEffect(() => {
    if (state === "sent" || state === "failed") statusRef.current?.focus();
  }, [state]);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (state === "sending") return;
    const form = event.target;
    const data = Object.fromEntries(new FormData(form).entries());
    // The honeypot: a real person never fills a field they cannot see. Say
    // nothing useful to whatever did, and do not post it on.
    if (data["bot-field"]) {
      setState("sent");
      return;
    }
    setState("sending");
    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": FORM_NAME, ...data }),
      });
      if (!response.ok) throw new Error(String(response.status));
      setState("sent");
      form.reset();
    } catch {
      setState("failed");
    }
  };

  if (state === "sent") {
    return (
      <div className="pilot-form-done" ref={statusRef} tabIndex={-1} role="status">
        <span className="tiny-dot" />
        <h3>Thank you, that has reached us.</h3>
        <p>
          We read every enquiry ourselves. Expect a reply from a person, not an
          autoresponder, and we will ask about your course before we suggest
          anything.
        </p>
        <button type="button" className="text-link" onClick={() => setState("idle")}>
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form
      className="pilot-form"
      name={FORM_NAME}
      method="POST"
      data-netlify="true"
      data-netlify-honeypot="bot-field"
      onSubmit={onSubmit}
      noValidate={false}
    >
      {/* Netlify routes a submission by this field, so it travels with the body. */}
      <input type="hidden" name="form-name" value={FORM_NAME} />
      <p className="pilot-form-hp" aria-hidden="true">
        <label>
          Leave this field empty
          <input name="bot-field" tabIndex={-1} autoComplete="off" />
        </label>
      </p>

      <div className="pilot-form-row">
        <label htmlFor="pf-name">
          <span className="pilot-form-caption">
            Your name <span aria-hidden="true">*</span>
          </span>
          <input id="pf-name" name="name" type="text" required autoComplete="name" />
        </label>
        <label htmlFor="pf-role">
          <span className="pilot-form-caption">Your role</span>
          <select id="pf-role" name="role" defaultValue="">
            <option value="">Select</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label htmlFor="pf-club">
        <span className="pilot-form-caption">
          Club or course <span aria-hidden="true">*</span>
        </span>
        <input id="pf-club" name="club" type="text" required autoComplete="organization" />
      </label>

      <div className="pilot-form-row">
        <label htmlFor="pf-email">
          <span className="pilot-form-caption">
            Email <span aria-hidden="true">*</span>
          </span>
          <input id="pf-email" name="email" type="email" required autoComplete="email" />
        </label>
        <label htmlFor="pf-phone">
          <span className="pilot-form-caption">
            Phone <span className="pilot-form-optional">optional</span>
          </span>
          <input id="pf-phone" name="phone" type="tel" autoComplete="tel" />
        </label>
      </div>

      <label htmlFor="pf-message">
        <span className="pilot-form-caption">
          What would you want a pilot to answer?
          <span className="pilot-form-optional">optional</span>
        </span>
        <textarea
          id="pf-message"
          name="message"
          rows={3}
          placeholder="The greens on the back nine have been slow to recover, and we would like to see where."
        />
      </label>

      {state === "failed" && (
        <p className="pilot-form-error" ref={statusRef} tabIndex={-1} role="alert">
          That did not send, so we have not received it. Please try again in a
          moment
          {siteConfig.enquiryUrl ? (
            <>
              , or <a href={siteConfig.enquiryUrl}>contact us directly</a>.
            </>
          ) : (
            "."
          )}
        </p>
      )}

      <button type="submit" className="button-primary" disabled={state === "sending"}>
        {state === "sending" ? "Sending" : "Request a pilot"}
      </button>

      <p className="pilot-form-privacy">
        We use these details only to talk to you about a VecTurf pilot. No list,
        no sharing, and we delete them if you ask.
      </p>
    </form>
  );
}
